import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './Gauge.css'; // Assuming you put the CSS in a file named Gauge.css

// The Gauge Component
const GaugeSlider = ({ onChange, onNext, onFocus }) => {
  const [current, setCurrent] = useState(0);
  const [pressed, setPressed] = useState(false);
  const animationRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // Keep a stable ref to the latest onChange to avoid effect loops from changing function identity
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const startOffset = 90;

  // Function to calculate the needle's rotation angle
  const calculateAngle = useCallback((value) => {
    return value * 1.8 - startOffset;
  }, [startOffset]);

  // Notify parent on value change (avoid depending on onChange identity)
  useEffect(() => {
    if (typeof onChangeRef.current === 'function') {
      onChangeRef.current(Math.round(current));
    }
  }, [current]);


  // Animate forward only on press (rating mechanism)
  const animateGauge = useCallback(() => {
    if (!pressed) return;

    setCurrent(prevCurrent => {
      if (prevCurrent < 100) {
        return prevCurrent + 0.5;
      } else {
        // Stop animation when reaching 100
        setPressed(false);
        cancelAnimationFrame(animationRef.current);
        return 100;
      }
    });

    animationRef.current = requestAnimationFrame(animateGauge);
  }, [pressed]);

  useEffect(() => {
    if (pressed) {
      // Start the rating animation when pressed is true
      animationRef.current = requestAnimationFrame(animateGauge);
    } else {
      // Stop the animation when pressed is false
      cancelAnimationFrame(animationRef.current);
    }

    // Cleanup function
    return () => cancelAnimationFrame(animationRef.current);
  }, [pressed, animateGauge]);

  // Event Handlers
  const handlePressStart = (e) => {
    if (e.type === 'touchstart') e.preventDefault(); // Prevent default on touch for better control
    if (onFocus) onFocus();
    setPressed(true);
  };

  const [showNext, setShowNext] = useState(false);
  const handlePressEnd = () => {
    setPressed(false);
    setShowNext(true);
  };

  const scoreDisplay = `Score: ${Math.round(current)}`;

  const needleStyle = {
    '--angle': `${calculateAngle(current)}deg`,
    transform: `translate(-50%, -50%) rotate(${calculateAngle(current)}deg)`,
  };

  return (
    <>
      <div className="meter">
        <div className="reading">{Math.round(current)}</div>

        <div className="colors-container">
          <div className="color-wheel"></div>
        </div>

        <div className="knop"></div>
        <div
          className="needle"
          style={needleStyle}
          onMouseDown={handlePressStart}
          onMouseUp={handlePressEnd}
          onMouseLeave={handlePressEnd}
          onTouchStart={handlePressStart}
          onTouchEnd={handlePressEnd}
        >
          <div className="pointer"></div>
        </div>
      </div>

      <div id="scoreDisplay" className="flex items-center justify-center mt-4 score">
        {!pressed && !showNext && (
          <span className="text-base md:text-lg text-white/80">Press and hold meter needle to rate</span>
        )}
        {showNext && (
          <>
            <span className="mr-4 text-base md:text-lg text-white/80">{scoreDisplay}</span>
            <button
              className="bubble-button px-4 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base font-semibold transition-all duration-300 bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white shadow-[0_10px_40px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.5)] hover:scale-105"
              onClick={() => {
                if (typeof onNext === 'function') onNext(Math.round(current));
              }}
            >
              <span className="relative z-10 flex items-center gap-1.5 md:gap-2">
                Next
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-4 md:h-4">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <div className="bubbles">
                {Array.from({ length: 24 }).map((_, i) => (
                  <span className="bubble" key={i}></span>
                ))}
              </div>
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default GaugeSlider;

GaugeSlider.propTypes = {
  onChange: PropTypes.func,
  onNext: PropTypes.func,
  onFocus: PropTypes.func,
};